// geo.ts — where is this phone (Oct 27, 2026).
//
// One place for the canvas's GPS: the shift start, the 5-minute pings, the
// "you are here" dot and the location check on a canvasser's home all read it.
//
// Why it exists: Michael Lewis (canvasser) went on shift on Oct 27 and the
// app recorded no start point and no pings. Every caller used to fire a cold
// getCurrentPosition with an 8-second limit and swallow the error, so a phone
// with location turned off for the site looked exactly like a slow fix, and
// nobody was told. Now:
//   • a running watch keeps a recent fix, which pings and shift start reuse;
//   • a cold lookup tries precise for 15 s, then a coarse fix, before failing;
//   • a failure says which problem it is ("denied" is a phone setting the rep
//     can fix, the rest are signal), so the page can show the right steps.

import { useCallback, useEffect, useRef, useState } from "react";

export type GeoProblem = "denied" | "unavailable" | "timeout" | "unsupported";
export interface Fix { lat: number; lon: number; accuracy_m?: number; speed_mps?: number; at: number }
export type GeoResult = { ok: true; fix: Fix } | { ok: false; problem: GeoProblem };
export type GeoPermission = "granted" | "denied" | "prompt" | "unknown";

let lastFix: Fix | null = null;
// Hooks that follow the phone hear about fixes any lookup gets (a shift start
// can succeed after the watch was refused — the watch then needs restarting).
const fixListeners = new Set<(f: Fix) => void>();

function toFix(p: GeolocationPosition): Fix {
  const { latitude, longitude, accuracy, speed } = p.coords;
  return {
    lat: latitude,
    lon: longitude,
    accuracy_m: typeof accuracy === "number" ? Math.round(accuracy) : undefined,
    speed_mps: typeof speed === "number" && speed >= 0 ? speed : undefined,
    at: Date.now(),
  };
}

function problemOf(e: GeolocationPositionError | null | undefined): GeoProblem {
  if (!e) return "unavailable";
  if (e.code === 1) return "denied";
  if (e.code === 3) return "timeout";
  return "unavailable";
}

/** Store a fix so the next ping / shift start can reuse it. */
export function rememberFix(p: GeolocationPosition): Fix {
  const f = toFix(p);
  lastFix = f;
  fixListeners.forEach((fn) => fn(f));
  return f;
}

/** The last fix any watch or lookup got, if it's younger than maxAgeMs. */
export function recentFix(maxAgeMs: number): Fix | null {
  return lastFix && Date.now() - lastFix.at <= maxAgeMs ? lastFix : null;
}

function once(opts: PositionOptions, hardMs: number): Promise<GeoResult> {
  return new Promise((resolve) => {
    let done = false;
    const fin = (r: GeoResult) => { if (!done) { done = true; resolve(r); } };
    navigator.geolocation.getCurrentPosition(
      (p) => fin({ ok: true, fix: rememberFix(p) }),
      (e) => fin({ ok: false, problem: problemOf(e) }),
      opts,
    );
    // iOS Safari can hang without calling either callback.
    setTimeout(() => fin({ ok: false, problem: "timeout" }), hardMs);
  });
}

/**
 * Where is the phone right now. Reuses a fix under 30 s old, otherwise asks
 * for a precise one (this is also what makes the phone show its "Allow
 * location?" prompt), then falls back to a coarse one on a slow signal.
 */
export async function locate(opts: { maxAgeMs?: number; timeoutMs?: number; coarseFallback?: boolean } = {}): Promise<GeoResult> {
  const fresh = recentFix(opts.maxAgeMs ?? 30_000);
  if (fresh) return { ok: true, fix: fresh };
  if (typeof navigator === "undefined" || !navigator.geolocation) return { ok: false, problem: "unsupported" };
  const t = opts.timeoutMs ?? 15_000;
  const precise = await once({ enableHighAccuracy: true, timeout: t, maximumAge: 30_000 }, t + 1_000);
  if (precise.ok || precise.problem === "denied" || opts.coarseFallback === false) return precise;
  const coarse = await once({ enableHighAccuracy: false, timeout: 10_000, maximumAge: 120_000 }, 11_000);
  // A refusal on the second try is the real answer (the first can time out
  // while the phone's "Allow location?" prompt is still on screen).
  return coarse.ok || coarse.problem === "denied" ? coarse : precise;
}

/** The browser's own answer to "may this site use location", where it says. */
export async function permissionState(): Promise<GeoPermission> {
  try {
    const st = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
    return (st?.state as GeoPermission) ?? "unknown";
  } catch {
    return "unknown";
  }
}

/** Live permission state; re-reads when the rep changes it or comes back to the tab. */
export function useGeoPermission(): GeoPermission {
  const [state, setState] = useState<GeoPermission>("unknown");
  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;
    const read = () => { void permissionState().then((s) => { if (!cancelled) setState(s); }); };
    read();
    navigator.permissions?.query({ name: "geolocation" as PermissionName })
      .then((st) => { status = st; st.onchange = read; })
      .catch(() => { /* Permissions API missing: stays "unknown" */ });
    const onVis = () => { if (document.visibilityState === "visible") read(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);
  return state;
}

/**
 * Follow the phone while `active`: the latest fix, or the problem stopping it.
 * retry() asks again (shows the "Allow location?" prompt when the phone still
 * can) and restarts the watch.
 */
export function useMyLocation(active: boolean): { fix: Fix | null; problem: GeoProblem | null; retry: () => Promise<GeoResult> } {
  const [fix, setFix] = useState<Fix | null>(null);
  const [problem, setProblem] = useState<GeoProblem | null>(null);
  const [round, setRound] = useState(0);
  const deniedRef = useRef(false);
  deniedRef.current = problem === "denied";

  // A fix from any other lookup (e.g. the shift start) counts too, and a watch
  // the phone refused earlier is restarted now that location works.
  useEffect(() => {
    if (!active) return;
    const onFix = (f: Fix) => {
      setFix(f);
      setProblem(null);
      if (deniedRef.current) setRound((n) => n + 1);
    };
    fixListeners.add(onFix);
    return () => { fixListeners.delete(onFix); };
  }, [active]);

  useEffect(() => {
    if (!active) { setFix(null); setProblem(null); return; }
    if (typeof navigator === "undefined" || !navigator.geolocation) { setProblem("unsupported"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setFix(rememberFix(p)); setProblem(null); },
      (e) => {
        const pr = problemOf(e);
        // A watch times out between fixes on a weak signal; that only matters
        // while we have nothing at all to show.
        if (pr === "denied") { setFix(null); setProblem("denied"); }
        else setProblem((cur) => (cur === "denied" ? cur : pr));
      },
      { enableHighAccuracy: true, maximumAge: 15_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [active, round]);

  const retry = useCallback(async () => {
    const r = await locate({ maxAgeMs: 0 });
    if (r.ok) { setFix(r.fix); setProblem(null); } else setProblem(r.problem);
    setRound((n) => n + 1);
    return r;
  }, []);

  return { fix, problem: fix ? null : problem, retry };
}

/** One line on what's wrong, for a banner. */
export function problemText(p: GeoProblem): string {
  switch (p) {
    case "denied": return "Location is turned off for this site, so the map can't show you and your shift isn't tracked.";
    case "unsupported": return "This browser can't share your location. Open the CRM in Safari (iPhone) or Chrome (Android).";
    case "timeout": return "Still looking for a GPS signal. Step outside or away from tall buildings.";
    default: return "Your phone isn't giving a location right now. Check that Location Services is on.";
  }
}

export type PhoneKind = "iphone-safari" | "iphone-chrome" | "android" | "other";
export function phoneKind(): PhoneKind {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document);
  if (iOS) return /CriOS/.test(ua) ? "iphone-chrome" : "iphone-safari";
  if (/Android/.test(ua)) return "android";
  return "other";
}

/** Steps to turn location back on, for the phone this page is open on. */
export function locationSteps(kind: PhoneKind): string[] {
  switch (kind) {
    case "iphone-safari":
      return [
        "Open the Settings app → Privacy & Security → Location Services. Make sure Location Services is On.",
        "In that list, tap Safari Websites → choose \"While Using the App\" and turn on Precise Location.",
        "Back in Safari on this page, tap the button at the left of the address bar (aA) → Website Settings → Location → Allow.",
        "Come back here and tap Try again.",
      ];
    case "iphone-chrome":
      return [
        "Open the Settings app → Privacy & Security → Location Services. Make sure it's On.",
        "In that list, tap Chrome → \"While Using the App\" and turn on Precise Location.",
        "Come back here and tap Try again. If Chrome asks, tap Allow.",
      ];
    case "android":
      return [
        "Swipe down from the top of the screen and make sure Location is on.",
        "In Chrome, tap the icon at the left of the address bar → Permissions → Location → Allow.",
        "Come back here and tap Try again.",
      ];
    default:
      return [
        "Turn on location for this browser in your device's settings.",
        "Click the icon at the left of the address bar → Location → Allow.",
        "Then tap Try again.",
      ];
  }
}
